import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertTriangle } from 'lucide-react';

import {
  GRID,
  MAX_SLICES,
  SERIES,
  SURFACE,
  TEXT_SECONDARY,
  axisProps,
  formatTick,
  formatValue,
  tooltipProps,
} from './chartTheme';

// Rows come from the backend transforms (app/services/dashboards/transforms.py):
// kpi -> [{ value }], bar/pie/line -> [{ key, value }], table -> flat rows.

const MAX_TABLE_COLUMNS = 8;

const foldSlices = (rows) => {
  if (rows.length <= MAX_SLICES + 1) return rows;
  const rest = rows
    .slice(MAX_SLICES)
    .reduce((sum, r) => sum + (Number(r.value) || 0), 0);
  return [...rows.slice(0, MAX_SLICES), { key: 'Other', value: rest }];
};

const Empty = () => (
  <div className="flex h-full items-center justify-center text-xs text-zinc-500">
    No data
  </div>
);

// What the viewer can do about each error code (app/services/dashboards/data.py).
const RETRYABLE = new Set(['not_connected', 'rate_limited', 'unavailable']);
const HINTS = {
  not_connected: 'Connect it from Integrations in chat, then retry.',
  no_access: 'Ask a project admin for access.',
};

const tileButton =
  'rounded-md border border-zinc-700 px-2 py-1 text-[11px] font-medium text-zinc-200 hover:bg-zinc-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#5652CF]';

/** `error` is the plain message; `detail` (owner only) is the tool's own text. */
export const ErrorTile = ({ error, code, detail, onRetry, onEdit }) => (
  <div
    role="alert"
    className="flex h-full flex-col items-center justify-center gap-2 overflow-auto px-4 text-center"
  >
    <AlertTriangle size={18} className="text-amber-400" aria-hidden="true" />
    <span className="text-xs font-medium text-zinc-200">
      {error || 'This widget could not load.'}
    </span>
    {HINTS[code] && (
      <span className="text-[11px] text-zinc-400">{HINTS[code]}</span>
    )}
    {((onRetry && RETRYABLE.has(code)) || (onEdit && code === 'bad_query')) && (
      <div className="no-drag flex gap-2">
        {onRetry && RETRYABLE.has(code) && (
          <button type="button" onClick={onRetry} className={tileButton}>
            Retry
          </button>
        )}
        {onEdit && code === 'bad_query' && (
          <button type="button" onClick={onEdit} className={tileButton}>
            Edit query
          </button>
        )}
      </div>
    )}
    {detail && (
      <details className="no-drag max-w-full text-[11px] text-zinc-500">
        <summary className="cursor-pointer hover:text-zinc-300">
          Details
        </summary>
        <p className="mt-1 break-words text-left">{detail}</p>
      </details>
    )}
  </div>
);

const LoadingTile = () => (
  <div className="flex h-full items-center justify-center">
    <div className="h-3 w-24 animate-pulse rounded bg-zinc-800" />
  </div>
);

const Kpi = ({ rows }) => {
  const value = rows?.[0]?.value;
  if (value === undefined) return <Empty />;
  return (
    <div className="flex h-full items-center px-1">
      <span className="text-4xl font-semibold tabular-nums text-zinc-100">
        {formatValue(value)}
      </span>
    </div>
  );
};

const BarWidget = ({ rows }) => {
  if (!rows?.length) return <Empty />;
  // Horizontal bars: group names stay readable however long they are.
  const labelWidth = Math.min(
    140,
    Math.max(...rows.map((r) => String(r.key).length)) * 7 + 8,
  );
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={rows}
        layout="vertical"
        margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
        barCategoryGap={2}
      >
        <CartesianGrid horizontal={false} stroke={GRID} />
        <XAxis type="number" tickFormatter={formatTick} {...axisProps} />
        <YAxis
          type="category"
          dataKey="key"
          width={labelWidth}
          interval={0}
          {...axisProps}
        />
        <Tooltip
          cursor={{ fill: '#27272a80' }}
          formatter={(v) => [formatValue(v), 'Value']}
          {...tooltipProps}
        />
        <Bar
          dataKey="value"
          fill={SERIES[0]}
          radius={[0, 4, 4, 0]}
          maxBarSize={22}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

const LineWidget = ({ rows }) => {
  if (!rows?.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
        <CartesianGrid vertical={false} stroke={GRID} />
        <XAxis dataKey="key" minTickGap={24} {...axisProps} />
        <YAxis tickFormatter={formatTick} width={40} {...axisProps} />
        <Tooltip
          cursor={{ stroke: TEXT_SECONDARY, strokeDasharray: '3 3' }}
          formatter={(v) => [formatValue(v), 'Value']}
          {...tooltipProps}
        />
        <Line
          type="monotone"
          dataKey="value"
          stroke={SERIES[0]}
          strokeWidth={2}
          dot={rows.length <= 31 ? { r: 3, strokeWidth: 0 } : false}
          activeDot={{ r: 5, stroke: SURFACE, strokeWidth: 2 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

const PieWidget = ({ rows }) => {
  const slices = foldSlices(rows || []);
  if (!slices.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={slices}
          dataKey="value"
          nameKey="key"
          innerRadius="55%"
          outerRadius="85%"
          stroke={SURFACE}
          strokeWidth={2}
          isAnimationActive={false}
        >
          {slices.map((s, i) => (
            <Cell key={s.key} fill={SERIES[i]} />
          ))}
        </Pie>
        <Tooltip
          formatter={(v, name) => [formatValue(v), name]}
          {...tooltipProps}
        />
        <Legend
          layout="vertical"
          align="right"
          verticalAlign="middle"
          iconType="circle"
          iconSize={8}
          formatter={(name) => (
            <span className="text-xs text-zinc-300">{name}</span>
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

const TableWidget = ({ rows }) => {
  if (!rows?.length) return <Empty />;
  const columns = Object.keys(rows[0]).slice(0, MAX_TABLE_COLUMNS);
  return (
    <div className="h-full overflow-auto">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-zinc-900">
          <tr>
            {columns.map((c) => (
              <th
                key={c}
                className="whitespace-nowrap border-b border-zinc-800 px-2 py-1.5 font-medium text-zinc-400"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={i} className="hover:bg-zinc-800/40">
              {columns.map((c) => (
                <td
                  key={c}
                  className="max-w-[240px] truncate border-b border-zinc-800/60 px-2 py-1.5 text-zinc-200"
                  title={formatValue(row[c])}
                >
                  {formatValue(row[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TextWidget = ({ text }) => (
  <div className="prose prose-sm prose-invert h-full max-w-none overflow-auto">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{text || ''}</ReactMarkdown>
  </div>
);

const BODY = {
  kpi: Kpi,
  bar: BarWidget,
  line: LineWidget,
  pie: PieWidget,
  table: TableWidget,
};

/** One tile. `data` is this widget's entry from /data: { rows, ... } | { error, code, detail } | undefined (loading).
 * `pending`: added in edit mode and not saved yet, so it has no data to load.
 * `onRetry` / `onEdit`: recovery actions for an error tile; leave out where they do not apply. */
const DashboardWidget = ({
  widget,
  data,
  pending = false,
  onRetry,
  onEdit,
}) => {
  let body;
  if (widget.type === 'text') body = <TextWidget text={widget.text} />;
  else if (pending)
    body = (
      <div className="flex h-full items-center justify-center text-xs text-zinc-500">
        Data loads after Save
      </div>
    );
  else if (!data) body = <LoadingTile />;
  else if (data.error)
    body = (
      <ErrorTile
        error={data.error}
        code={data.code}
        detail={data.detail}
        onRetry={onRetry}
        onEdit={onEdit}
      />
    );
  else {
    const Body = BODY[widget.type];
    body = Body ? <Body rows={data.rows} /> : <Empty />;
  }

  return (
    <section
      aria-label={widget.title}
      className="flex h-full flex-col overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900 p-3"
    >
      <header className="mb-2 flex items-baseline justify-between gap-2">
        <h3 className="truncate text-xs font-semibold text-zinc-300">
          {widget.title}
        </h3>
        {data?.truncated && (
          <span
            className="shrink-0 text-[10px] text-zinc-500"
            title="Only the first rows were read"
          >
            partial
          </span>
        )}
      </header>
      <div className="min-h-0 flex-1">{body}</div>
    </section>
  );
};

export default DashboardWidget;
